import { CheckoutError, CrossRegionRedirection } from './checkout'
import { Currency, SupportedLocale } from './country'
import { DocumentVerificationResult, IdentityCheckStatus, IdentityCheckStatuses } from '@afterpaytouch/portal-api/identity/types'
import { Country, CreditFacilityStatus, ErrorCode, PaymentMethodType, OrderSource, FacilityApplicationEligibility } from '@afterpaytouch/portal-api/types'
import { UserLookupResponse } from '@afterpaytouch/portal-api/consumer/lookup/types'
import { ConsumerAddressType, Money } from '@afterpay/types'
import type { CheckoutActionLookupResponse } from '../state/consumer/actions'
import { PublicResponse } from '@afterpay/verify-service-api'

export interface ConsumerAddress {
  id: number
  name: string
  address1: string
  address2: string | null
  area2: string | null
  suburb: string
  state: string
  postcode: string
  countryCode: Country
  addressType?: ConsumerAddressType
}

export enum AccountStatus {
  NO_VALUE = 'NO_VALUE',
  NEW = 'NEW',
  REGISTERED = 'REGISTERED',
  UNREGISTERED = 'UNREGISTERED',
  DISABLED = 'DISABLED',
  ON_HOLD = 'ON_HOLD',
  SUSPENDED = 'SUSPENDED',
  CLOSED = 'CLOSED',
  WRITTEN_OFF = 'WRITTEN_OFF',
  HARDSHIP = 'HARDSHIP',
  PAYMENT_ARRANGEMENT = 'PAYMENT_ARRANGEMENT',
  POLICY_HOLD = 'POLICY_HOLD',
}

export interface IdentityCheckMetadata {
  profileCheckAttemptsLeft: boolean
}
export interface ConsumerAccountResponse {
  accountStatus: AccountStatus
  contactAddress: ConsumerAddress
  countryCode: Country
  creditCheckConsented?: boolean
  dateOfBirth: string
  documentVerificationResults?: DocumentVerificationResult[]
  // frozenStatus: TODO: where is this used?
  email: string
  givenNames: string
  id: number
  identityCheckStatus: IdentityCheckStatus
  identityCheckStatuses: IdentityCheckStatuses
  identityCheckMetadata?: IdentityCheckMetadata
  latestFacilityStatus?: CreditFacilityStatus
  otherGivenNames: string
  phoneNumber: string | null
  preferredLocale: SupportedLocale
  preferredPaymentMethod: PaymentMethodType
  profileConfirmed: boolean
  smsVerificationEnabled: boolean
  smsVerificationRequired: boolean
  surname: string
  uuid: string
}

export interface CheckoutConsumerNames {
  givenNames: string
  surname: string
}

export interface CheckoutConsumerDetails extends CheckoutConsumerNames {
  phoneNumber: string
  email: string | null
}

export enum SingleUseCardSubSource {
  SINGLE_USE_PAYMENT = 'SINGLE_USE_PAYMENT',
  AFTERPAY_BUTTON = 'AFTERPAY_BUTTON',
  SUP_IN_CASH_APP = 'SUP_IN_CASH_APP',
}

export interface CheckoutMerchantDetails {
  name: string
  id: number
  callbackUrl: string
  callbackUrlQueryArgs: string
  cancelCallbackUrl: string
  failureCallbackUrl?: string
  popupOriginUrl: string | null
  source: OrderSource
  singleUseCardSubSource: SingleUseCardSubSource
  supportedCountries: Country[]
  merchantAvatarUrl: string | null
  merchantCategoryCode: number
  merchantCategoryName: string
  merchantIndustryName: string
  merchantDisplayName: string
  merchantCurrencyCode: string
}

export interface CheckoutAddress {
  name: string
  address1: string
  address2: string
  area2?: string
  suburb: string
  postcode: string
  state: string
  countryCode: Country
}

export const money = (amount: string, currency: Currency = 'AUD'): Money => ({
  amount,
  currency,
  symbol: currency === 'GBP' ? '£' : '$',
})

export interface EmailLookupRequest {
  email: string
  amplitudeSessionId?: number
  transactionToken?: string
}

export interface VerifyAccountMobileRequest {
  mobile: string
}

export interface ConfirmVerifyAccountMobile {
  mobile: string
  smsCode: string
}

export interface UpdateAccountDetails {
  givenNames: string
  otherGivenNames: string
  surname: string
  dateOfBirth: string
  address1: string
  address2: string
  area2: string
  countryCode: Country
  postcode: string
  state: string
  suburb: string
}
export interface AccountKYCRefreshRequest {
  address1: string
  address2: string
  area2: string
  countryCode: Country
  postcode: string
  state: string
  suburb: string
}
export interface UpdateAccountDetailsRequest extends UpdateAccountDetails {
  preferredLocale: SupportedLocale
  creditPolicyAgree: boolean
}

// TODO move this to a common file between registration and verify retry

export enum ProfileErrorCode {
  Default = 'default',
  InvalidAddress = 'invalidAddress',
  ExistingPhoneNumber = 'existingPhoneNumber',
  ExistingPhoneNumberSendEmail = 'existingPhoneNumberSendEmail',
  SameProfileInfoWarning = 'sameProfileInfoWarning',
}

export const ProfileErrors: Partial<Record<ErrorCode, ProfileErrorCode>> = {
  [ErrorCode.IneligibleState]: ProfileErrorCode.InvalidAddress,
  [ErrorCode.InvalidAddress]: ProfileErrorCode.InvalidAddress,
  [ErrorCode.AddressPoBoxNotAllowed]: ProfileErrorCode.InvalidAddress,
  [ErrorCode.InvalidObject]: ProfileErrorCode.InvalidAddress,
  [ErrorCode.AddressSuburbRequired]: ProfileErrorCode.InvalidAddress,
  [ErrorCode.PostcodeInvalid]: ProfileErrorCode.InvalidAddress,
  [ErrorCode.AddressStateInvalid]: ProfileErrorCode.InvalidAddress,
  [ErrorCode.InvalidLocalAreaOrVillageName]: ProfileErrorCode.InvalidAddress,
  [ErrorCode.ExistingPhoneNumber]: ProfileErrorCode.ExistingPhoneNumber,
  [ErrorCode.ExistingPhoneNumberSendEmail]: ProfileErrorCode.ExistingPhoneNumberSendEmail,
}

export enum UserNameType {
  Mobile = 'mobile',
  Email = 'email',
}

export const LoginIdentityErrors: Partial<Record<ErrorCode, string>> = {
  [ErrorCode.InvalidEmailAddress]: 'emailNotValid',
  [ErrorCode.RegistrationNotPermitted]: 'registrationNotPermitted',
}

export type HandleUserLookupResult = UserLookupResponse | CrossRegionRedirection | CheckoutError | CheckoutActionLookupResponse | PublicResponse

// HandleEmailLookup / HandleUserLookupResult type guard
export const isUserLookupResponseType = (obj: any): obj is UserLookupResponse => 'existing' in obj
export const isCheckoutErrorType = (obj: any): obj is CheckoutError => 'error' in obj
export const isCrossRegionRedirectionType = (obj: any): obj is CrossRegionRedirection => 'crossRegionCheckoutUrl' in obj
export const isPublicResponse = (obj: any): obj is PublicResponse => 'code' in obj && 'message' in obj

// This is only used for Analytics purposes
export enum FacilityCohort {
  LCCC_EXISTING_USER_FACILITY_CLOSED = 'LCCC_EXISTING_USER_FACILITY_CLOSED ',
  LCCC_EXISTING_USER_FACILITY_UNKNOWN = 'LCCC_EXISTING_USER_FACILITY_UNKNOWN',
  LCCC_EXISTING_USER_FACILITY_NONE = 'LCCC_EXISTING_USER_FACILITY_NONE',
  LCCC_EXISTING_USER_FACILITY_OPEN = 'LCCC_EXISTING_USER_FACILITY_OPEN',

  LCCC_NEW_USER_FACILITY_CLOSED = 'LCCC_NEW_USER_FACILITY_CLOSED ',
  LCCC_NEW_USER_FACILITY_UNKNOWN = 'LCCC_NEW_USER_FACILITY_UNKNOWN',
  LCCC_NEW_USER_FACILITY_NONE = 'LCCC_NEW_USER_FACILITY_NONE',
  LCCC_NEW_USER_FACILITY_OPEN = 'LCCC_NEW_USER_FACILITY_OPEN',
}
export interface ConsumerFacilityCohort {
  cohort: FacilityCohort
  getStartedContext: GetStartedContext
  eligibility: FacilityApplicationEligibility
  /** True when the user entered the LCCC onboarding as a NEW_USER in this checkout session
   *  and was later promoted to LCCC_EXISTING after startCheckout succeeded. Used to ensure
   *  the "Step 2 of 2" interstitial is still shown before redirecting to credit-application. */
  enteredAsNewUser?: boolean
}

export type GetStartedContext = 'LCCC_EXISTING' | 'NEW_USER'
